import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { EmptyState } from '../ui/empty-state';
import { Clock, PhoneCall, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const AgentBehaviorTimeline = ({ filters, reducedMotion }) => {
  const [loading, setLoading] = useState(true);
  const [timelineData, setTimelineData] = useState(null);

  useEffect(() => {
    fetchBehaviorTimeline();
  }, [filters]);

  // TODO Phase Audit Fix B: wire to real /api/analytics/agent-behavior-timeline endpoint when backend implements it.
  const fetchBehaviorTimeline = async () => {
    setLoading(true);
    try {
      // No real endpoint yet — leave data empty so the EmptyState renders.
      setTimelineData([]);
    } catch (error) {
      console.error('Failed to fetch behavior timeline:', error);
      setTimelineData([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckCircle className="w-5 h-5 text-emerald-400" />;
      case 'error': return <XCircle className="w-5 h-5 text-red-400" />;
      case 'warning': return <AlertCircle className="w-5 h-5 text-amber-400" />;
      default: return <PhoneCall className="w-5 h-5 text-ink-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'border-emerald-500/30 bg-emerald-500/10';
      case 'error': return 'border-red-500/30 bg-red-500/10';
      case 'warning': return 'border-amber-500/30 bg-amber-500/10';
      default: return 'border-ink-300 dark:border-ink-700 bg-ink-500';
    }
  };

  return (
    <Card className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50 backdrop-blur-xl" data-testid="agent-behavior-timeline-card">
      <CardHeader>
        <CardTitle className="text-xl text-slate-100 flex items-center gap-2">
          <Clock className="w-5 h-5 text-ink-500" />
          Agent Behavior Timeline
        </CardTitle>
        <CardDescription className="text-slate-400 mt-1">
          Real-time chronological view of agent activities
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-slate-800/30 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : !timelineData || timelineData.length === 0 ? (
          <EmptyState
            icon={Clock}
            title="No data yet"
            description="Data will appear once it flows through."
          />
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-[30px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-ink-7000 via-ink-5000 to-transparent" />

            <div className="space-y-4">
              {timelineData.map((item, index) => (
                <div
                  key={index}
                  className="relative pl-16"
                  style={{
                    animation: reducedMotion ? 'none' : `fadeIn 0.3s ease-out ${index * 0.1}s both`
                  }}
                  data-testid={`timeline-event-${index}`}
                >
                  {/* Icon */}
                  <div className={`absolute left-[18px] top-2 p-2 rounded-full border-2 ${getStatusColor(item.status)}`}>
                    {getStatusIcon(item.status)}
                  </div>

                  {/* Content */}
                  <div className={`p-4 rounded-lg border ${getStatusColor(item.status)}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-slate-100" data-testid="event-name">{item.event}</p>
                        <p className="text-sm text-slate-400 mt-1">{item.agent}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-400" data-testid="event-time">{item.time}</p>
                        <p className="text-xs text-slate-500 mt-1">{item.duration}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-700/30">
                      <p className="text-xs text-slate-400">Outcome:</p>
                      <p className="text-sm text-slate-300" data-testid="event-outcome">{item.outcome}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AgentBehaviorTimeline;